/**
 * Reel inspection modal (OutlierOS workbench modal, docs/legacy-ui/02 §1.3): player 46% / details 54%.
 * Player: saved video (R2) with poster, flicker-safe buffering overlay, automatic fallback to Instagram's embed
 * on real media errors, scrubber, ±5 s, speed, volume (hidden in embed mode). Details: wrap-around pager
 * (← → keys), bookmark, add to collection, metrics table, spoken hook, transcript (Roman toggle; transcribe /
 * free unlock), caption, storage countdown, open video, open on Instagram. Esc closes; Space plays/pauses.
 * Focus moves into the dialog and returns on close; the page behind does not scroll.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Bookmark, Check, ChevronLeft, ChevronRight, Copy, Download, ExternalLink, Film, Pause, Play, RotateCcw, RotateCw, Volume2, VolumeX, X, Zap,
} from 'lucide-react';
import { creditsForSeconds } from '@vw/core/src/credits';
import { collabLabel, engagement } from '@vw/core/src/report';
import { missingHookText } from '@vw/core/src/transcript';
import { formatWiseScore } from '@vw/core/src/score';
import { Button, TierBadge, cn, copyText, toast, type TierThresholds } from '@vw/ui';
import { compact, date, igReel } from '../../format';
import { InstagramEmbed } from '../InstagramEmbed';
import { api } from '../../api';
import { AddToCollection } from '../AddToCollection';
import { LANG, type InspectReel, type Transcript } from './types';
import { useTranscript } from './useTranscript';

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];
const mmss = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
const relative = (ms: number | null) => {
  if (!ms) return '';
  const d = (Date.now() - ms) / 86_400_000;
  if (d < 1 / 24) return 'just now';
  if (d < 1) return `${Math.round(d * 24)}h ago`;
  if (d < 31) return `${Math.round(d)}d ago`;
  if (d < 365) return `${Math.round(d / 30)} months ago`;
  return `${Math.round(d / 365)} year${d >= 730 ? 's' : ''} ago`;
};

export function ReelInspector({ reels, index, onIndex, onClose, handle, tiers, secondsPerCredit, retentionDays, saveVideos, bookmarked, onBookmark, onTranscribed, timeZone, scoreBasis, notEnough, noScore }: {
  reels: InspectReel[]; index: number; onIndex: (i: number) => void; onClose: () => void; handle: string; tiers: TierThresholds;
  secondsPerCredit: number; retentionDays: number;
  /** Decision 81: whether videos are saved at all (Admin's archive.saveVideos; absent = an older API, which saved them). */
  saveVideos?: boolean;
  bookmarked: Set<string>; onBookmark?: (pk: string, on: boolean) => void;
  onTranscribed?: (pk: string, t: Transcript) => void; timeZone?: string;
  /** The creator's score basis ('likes' for a creator whose reels show no views) and "Not enough reels yet (n/25)" (decision 71). */
  scoreBasis?: string | null; notEnough?: string;
  /** Decision 91: a collab-only profile's page: an unscored reel shows "–" with this reason (no baseline of its own yet). */
  noScore?: string;
}) {
  const r = reels[index]!;
  const n = reels.length;
  const collab = collabLabel(r); // decision 91: 'Collab with @handle'
  const { transcript, job, message, transcribe, queueMessage } = useTranscript(r.pk, r.hasTranscript, onTranscribed);
  const [roman, setRoman] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const prev = () => onIndex((index - 1 + n) % n);
  const next = () => onIndex((index + 1) % n);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const before = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialog.current?.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (before instanceof HTMLElement) before.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (t instanceof Element && t.closest('input, textarea, select')) return;
      if (e.key === 'Escape') closeRef.current();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  useEffect(() => setRoman(false), [r.pk]);

  const copy = async (key: string, text: string) => {
    if (!(await copyText(text, { quiet: true }))) return;
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  const tr = transcript && transcript !== 'loading' ? transcript : null;
  const showRoman = roman && !!tr?.romanText;
  const rawHook = tr?.hook ?? r.hook ?? null;
  // A "hook" that is just the caption is not a spoken hook.
  const hook = rawHook && rawHook.trim() !== (r.caption ?? '').trim() ? rawHook : null;
  const cost = creditsForSeconds(r.durationS ?? 60, secondsPerCredit);
  // Unknown length: the cost is an estimate for 60 s (the job charges the real length), so say "about" (decision 71).
  const costText = `${r.durationS === null ? 'about ' : ''}${cost} credit${cost === 1 ? '' : 's'}`;
  const usual = scoreBasis === 'likes' ? 'likes ÷ the creator\'s usual likes' : 'views ÷ the creator\'s usual views';
  const eng = engagement(r);
  // Decision 80: the audio track (or a video with sound) is what a transcription reads; older rows judge by the video.
  const canTranscribe = r.transcribable ?? r.videoUrl !== null;
  const daysLeft = r.videoUrl && r.videoSavedAt ? Math.ceil((r.videoSavedAt + retentionDays * 86_400_000 - Date.now()) / 86_400_000) : null;
  const isMarked = bookmarked.has(r.pk);

  async function toggleBookmark() {
    try {
      if (isMarked) await api(`/api/bookmarks/${r.pk}`, { method: 'DELETE' });
      else await api('/api/bookmarks', { method: 'POST', json: { reelPk: r.pk } });
      onBookmark?.(r.pk, !isMarked);
    } catch (e) {
      toast.error('Could not update the bookmark', { description: (e as Error).message });
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-2 backdrop-blur-md animate-fade-in sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} role="dialog" aria-modal="true" aria-label={`Reel by @${handle}`} className="flex outline-none max-h-[94vh] w-full max-w-6xl animate-zoom-in flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl md:h-[92vh] md:flex-row">
        <div className="relative h-[68vh] shrink-0 bg-black md:h-auto md:w-[46%]"><Player key={r.pk} r={r} /></div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <div className="sticky top-0 z-10 flex flex-wrap items-center gap-2 border-b border-border bg-card/95 p-4 backdrop-blur">
            <div className="flex items-center rounded-full border border-border">
              <button aria-label="Previous reel" title="Previous reel (←)" onClick={prev} className="rounded-l-full p-1.5 hover:bg-secondary"><ChevronLeft className="h-4 w-4" /></button>
              <span className="px-2 text-xs font-semibold tabular-nums">{index + 1} of {n}</span>
              <button aria-label="Next reel" title="Next reel (→)" onClick={next} className="rounded-r-full p-1.5 hover:bg-secondary"><ChevronRight className="h-4 w-4" /></button>
            </div>
            <button onClick={toggleBookmark} aria-pressed={isMarked} className={cn('inline-flex items-center gap-1 rounded-xl border px-2.5 py-1.5 text-xs font-semibold', isMarked ? 'border-amber-400/50 bg-amber-400/10 text-amber-600 dark:text-amber-400' : 'border-border hover:bg-secondary')}>
              <Bookmark className={cn('h-3.5 w-3.5', isMarked && 'fill-current')} />{isMarked ? 'Bookmarked' : 'Bookmark'}
            </button>
            <AddToCollection reelPks={[r.pk]} compact align="left" label="Collection" />
            <TierBadge score={r.outlierScore} tiers={tiers} />
            <button aria-label="Close" onClick={onClose} className="ml-auto rounded-full p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"><X className="h-5 w-5" /></button>
          </div>

          <div className="space-y-5 p-4 sm:p-5">
            <dl className="divide-y divide-border rounded-2xl border border-border text-sm">
              {([
                ['Views', r.views ? r.views.toLocaleString('en-US') : <span title="Instagram didn't return a view count for this reel. Rescan the creator to refresh it." className="text-muted-foreground">Not available</span>],
                ['Likes', r.likesHidden ? 'Hidden by creator' : compact(r.likes)],
                ['Comments', compact(r.comments)],
                ['Wise Score', r.outlierScore !== null ? <span className="font-bold text-amber-600 dark:text-amber-400" title={`Exactly ${r.outlierScore.toFixed(2)}×: ${usual}`}>{formatWiseScore(r.outlierScore)}</span> : r.verifying ? <span className="text-muted-foreground" title="Just scanned: scored once Instagram's thumbnail confirms the reel (usually seconds)">Verifying…</span> : noScore ? <span className="text-muted-foreground" title={noScore}>–</span> : <span className="text-muted-foreground" title={notEnough ?? 'Not enough recent reels to score yet'}>Not scored yet</span>],
                ['Virality', r.viralityScore !== null ? `${r.viralityScore} / 100` : '–'],
                ['Published', r.postedAt ? date(r.postedAt, timeZone) : 'Recently'],
                ['Engagement rate', eng !== null ? <span className="text-emerald-600 dark:text-emerald-400">{(eng * 100).toFixed(2)}%</span> : '—'],
              ] as Array<[string, React.ReactNode]>).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-2.5"><dt className="text-muted-foreground">{k}</dt><dd className="font-semibold tabular-nums">{v}</dd></div>
              ))}
            </dl>
            {(collab || r.isPaidPartnership || r.isPinned || r.audio) && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                {r.isPinned && <span className="rounded-md border border-border px-1.5 py-0.5">Pinned</span>}
                {collab && <span className="rounded-md border border-border px-1.5 py-0.5">{collab}</span>}
                {r.isPaidPartnership && <span className="rounded-md border border-border px-1.5 py-0.5">Paid partnership</span>}
                {r.audio && <span>♫ {r.audio.title ?? (r.audio.type === 'original' ? 'Original audio' : 'Audio')}{r.audio.artist ? ` · ${r.audio.artist}` : ''}</span>}
              </div>
            )}

            <section className="space-y-2 rounded-2xl border border-border bg-secondary/40 p-4">
              <div className="flex items-center justify-between"><h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">🎣 Spoken hook</h3>
                {hook && <CopyBtn on={copied === 'hook'} onClick={() => copy('hook', hook)} />}</div>
              <p lang={tr?.script === 'devanagari' || r.hookScript === 'devanagari' ? 'hi' : undefined} className={cn('text-sm', hook ? 'font-semibold italic' : 'text-muted-foreground')}>
                {hook ? `“${hook}”` : missingHookText(tr)}
              </p>
            </section>

            <section className="space-y-2 rounded-2xl border border-border p-4">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">📝 Transcript</h3>
                {tr && <span className="rounded-md bg-secondary px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">{LANG[tr.lang] ?? tr.lang}</span>}
                {tr?.romanText && <label className="ml-auto flex items-center gap-1.5 text-xs text-muted-foreground"><input type="checkbox" checked={showRoman} onChange={() => setRoman(!roman)} />Roman script</label>}
                {tr && <CopyBtn on={copied === 'tr'} onClick={() => copy('tr', showRoman ? tr.romanText! : tr.text)} />}
              </div>
              {transcript === 'loading' && <p className="text-sm text-muted-foreground">Loading…</p>}
              {tr && (
                <>
                  <p lang={showRoman || tr.script === 'latin' ? 'en' : tr.script === 'devanagari' ? 'hi' : undefined} className="max-h-48 overflow-y-auto whitespace-pre-line text-sm leading-relaxed">{showRoman ? tr.romanText : tr.text}</p>
                  {tr.needsReview && <p className="text-xs text-muted-foreground">This transcript may contain mistakes.</p>}
                </>
              )}
              {!transcript && !job && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">{message ?? (!canTranscribe ? "No saved audio for this reel yet. A scan with the extension saves the audio of every reel it reads; for this one, click its badge on the creator's Reels tab and press Transcribe in the side panel." : `Not transcribed yet. Transcribe it to get the full script (${costText}).`)}</p>
                  <Button onClick={transcribe} disabled={!canTranscribe}><Zap className="h-3.5 w-3.5" />Transcribe ({costText})</Button>
                </div>
              )}
              {job && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Zap className="h-3.5 w-3.5 shrink-0 animate-pulse text-primary" />{queueMessage ?? 'Transcribing… this usually takes under a minute.'}</p>}
            </section>

            <section className="space-y-2 rounded-2xl border border-border p-4">
              <div className="flex items-center justify-between"><h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">💬 Instagram caption</h3>
                {r.caption && <CopyBtn on={copied === 'cap'} onClick={() => copy('cap', r.caption!)} />}</div>
              <p className="max-h-32 overflow-y-auto whitespace-pre-line text-sm text-muted-foreground">{r.caption || 'No written caption from the creator.'}</p>
            </section>

            {r.videoUrl !== undefined && (
              <p className={cn('rounded-xl px-3 py-2 text-xs', daysLeft !== null && daysLeft <= 3 ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300' : 'bg-secondary/60 text-muted-foreground')}>
                {r.videoUrl === null ? (saveVideos === false
                  ? "Plays from Instagram. We keep each reel's audio for transcription, not its video."
                  : "This video has not been saved. A scan saves the video of every reel it reads; any reel can be transcribed from the extension's side panel.")
                  : r.videoSilent ? `This saved video is the small version without sound; transcription uses the reel's audio track. Kept ${daysLeft === null ? `for ${retentionDays} days after it is saved` : `${Math.max(0, daysLeft)} more day${daysLeft === 1 ? '' : 's'}`}.`
                  : daysLeft === null ? `Videos are kept for ${retentionDays} days after they are saved.`
                  : daysLeft <= 0 ? `This video is being removed today (${retentionDays}-day storage). Download or transcribe it now.`
                  : `Video kept for ${daysLeft} more day${daysLeft === 1 ? '' : 's'}: download or transcribe it before then.`}
              </p>
            )}

            <div className="grid grid-cols-2 gap-2">
              {/* Media is on another origin, where browsers ignore `download`: open it; the browser's own menu saves it. */}
              <a href={r.videoUrl ?? undefined} target="_blank" rel="noreferrer" referrerPolicy="no-referrer" aria-disabled={!r.videoUrl}
                className={cn('inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-xs font-semibold text-primary-foreground', !r.videoUrl && 'pointer-events-none opacity-40')}
                title={r.videoUrl ? 'Open the video in a new tab (save it from there)' : saveVideos === false ? 'Videos are not saved: open the reel on Instagram' : 'No saved video yet'}><Download className="h-3.5 w-3.5" />Open video</a>
              <a href={igReel(r.shortcode)} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-border px-3 py-2.5 text-xs font-semibold hover:bg-secondary">📸 Open on Instagram<ExternalLink className="h-3 w-3" /></a>
            </div>
            <p className="text-center text-xs text-muted-foreground">Posted {relative(r.postedAt)}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function CopyBtn({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-secondary hover:text-foreground">
      {on ? <><Check className="h-3 w-3 text-emerald-500" />Copied</> : <><Copy className="h-3 w-3" />Copy</>}
    </button>
  );
}

/** Video with buffering overlay and IG-embed fallback (OutlierOS player rules). */
function Player({ r }: { r: InspectReel }) {
  const [mode, setMode] = useState<'video' | 'embed'>(r.videoUrl ? 'video' : 'embed');
  const [buffering, setBuffering] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(r.durationS ?? 0);
  const [rate, setRate] = useState(1);
  const [vol, setVol] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speedOpen, setSpeedOpen] = useState(false);
  const v = useRef<HTMLVideoElement>(null);
  const speedRef = useRef<HTMLDivElement>(null);
  const lastTick = useRef(0);

  useEffect(() => {
    if (!speedOpen) return;
    const close = (e: MouseEvent) => !speedRef.current?.contains(e.target as Node) && setSpeedOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [speedOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target;
      if (e.key !== ' ' || mode !== 'video' || (t instanceof Element && t.closest('input, textarea, select, button'))) return;
      e.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  const toggle = () => {
    const el = v.current;
    if (!el) return;
    if (el.paused) void el.play().catch(() => undefined); else el.pause();
  };
  const seek = (t: number) => {
    const el = v.current;
    if (!el) return;
    el.currentTime = Math.max(0, Math.min(dur || el.duration || 0, t));
    setTime(el.currentTime);
  };

  return (
    <div className="flex h-full flex-col">
      <div className="relative flex min-h-0 flex-1 items-center justify-center">
        {mode === 'video' && r.videoUrl ? (
          <video ref={v} key={r.videoUrl} src={r.videoUrl} poster={r.thumbUrl ?? undefined} preload="metadata" playsInline onClick={toggle}
            className="h-full max-h-full w-auto max-w-full object-contain"
            onLoadedMetadata={(e) => {
              const el = e.currentTarget;
              setBuffering(false);
              if (Number.isFinite(el.duration)) setDur(el.duration);
              // Keep the chosen speed and volume after switching back from the embed.
              el.playbackRate = rate;
              el.volume = vol;
              el.muted = muted;
            }}
            onCanPlay={() => setBuffering(false)} onPlaying={() => { setBuffering(false); setPlaying(true); }} onPause={() => setPlaying(false)}
            onWaiting={() => { if (playing) setBuffering(true); }}
            onTimeUpdate={(e) => { const t = e.currentTarget.currentTime; if (Math.abs(t - lastTick.current) >= 0.25) { lastTick.current = t; setTime(t); } }}
            onError={(e) => { if (e.currentTarget.error?.code !== 1) { setMode('embed'); setBuffering(true); } }} />
        ) : (
          // No saved video (decision 81: the default, only the audio track is kept): Instagram's own embed, same size.
          <InstagramEmbed shortcode={r.shortcode} title="Instagram embed" className="aspect-[9/16] h-full max-w-full border-0 bg-white" onLoad={() => setBuffering(false)} />
        )}
        {buffering && (
          <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 text-center">
            <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-600 text-white">
              <Film className="h-6 w-6 animate-pulse" /><span className="absolute inset-0 animate-ping rounded-2xl ring-2 ring-indigo-400/50" />
            </span>
            <p className="text-sm font-semibold text-white">Getting your reel ready…</p>
          </div>
        )}
        {mode === 'video' && !playing && !buffering && (
          <button aria-label="Play" onClick={toggle} className="absolute flex h-16 w-16 items-center justify-center rounded-full bg-white/90 text-black shadow-xl"><Play className="ml-1 h-7 w-7 fill-black" /></button>
        )}
        {r.videoUrl && (
          <button onClick={() => { setMode(mode === 'video' ? 'embed' : 'video'); setBuffering(true); setPlaying(false); }}
            className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-lg border border-white/10 bg-black/70 px-2 py-1 text-[11px] font-semibold text-white backdrop-blur">
            <Film className="h-3 w-3" />{mode === 'video' ? 'Switch to Instagram embed' : 'Switch to saved video'}
          </button>
        )}
      </div>
      {mode === 'video' && r.videoUrl && (
        <div className="space-y-2 border-t border-white/10 bg-neutral-900 p-3 text-white">
          <input type="range" aria-label="Seek" min={0} max={dur || 0} step={0.1} value={Math.min(time, dur || 0)} onChange={(e) => seek(Number(e.target.value))} className="w-full accent-[rgb(var(--primary))]" />
          <div className="flex items-center gap-2 text-xs">
            <button aria-label={playing ? 'Pause' : 'Play'} onClick={toggle} className="rounded-lg p-1.5 hover:bg-white/10">{playing ? <Pause className="h-4 w-4 fill-white" /> : <Play className="h-4 w-4 fill-white" />}</button>
            <button aria-label="Back 5 seconds" onClick={() => seek(time - 5)} className="flex items-center gap-0.5 rounded-lg p-1.5 hover:bg-white/10"><RotateCcw className="h-3.5 w-3.5" />5s</button>
            <button aria-label="Forward 5 seconds" onClick={() => seek(time + 5)} className="flex items-center gap-0.5 rounded-lg p-1.5 hover:bg-white/10"><RotateCw className="h-3.5 w-3.5" />5s</button>
            <div className="relative" ref={speedRef}>
              <button onClick={() => setSpeedOpen(!speedOpen)} aria-expanded={speedOpen} className="rounded-lg px-2 py-1 font-semibold hover:bg-white/10">{rate}x</button>
              {speedOpen && (
                <div className="absolute bottom-8 left-0 rounded-lg border border-white/10 bg-neutral-800 p-1 shadow-xl">
                  {SPEEDS.map((s) => <button key={s} onClick={() => { setRate(s); if (v.current) v.current.playbackRate = s; setSpeedOpen(false); }} className={cn('block w-full rounded px-3 py-1 text-left', s === rate ? 'text-sky-400' : 'hover:bg-white/10')}>{s}x</button>)}
                </div>
              )}
            </div>
            <button aria-label={muted ? 'Unmute' : 'Mute'} onClick={() => { const m = !muted; setMuted(m); if (v.current) v.current.muted = m; }} className="rounded-lg p-1.5 hover:bg-white/10">{muted || vol === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}</button>
            <input type="range" aria-label="Volume" min={0} max={1} step={0.05} value={muted ? 0 : vol} className="w-14 accent-white"
              onChange={(e) => { const x = Number(e.target.value); setVol(x); setMuted(x === 0); if (v.current) { v.current.volume = x; v.current.muted = x === 0; } }} />
            <span className="ml-auto font-mono tabular-nums">{mmss(time)} / {mmss(dur || 0)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
